import React, { useEffect, useState } from 'react';

import { Box, Divider, Link, Stack, Tooltip, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ImageAvatar } from 'components/Avatar';
import { ListCard } from 'components/Card';
import { Status as StatusComponent } from 'components/Status';

import { isoToDateString } from 'utils/dateFormatter';

import { useAuth } from 'lib/auth';
import { localizeRelativeTime } from 'i18n/dayjs';
import { paths } from 'routes';

import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import { formatJobAdLocation, formatJobAdLocationShort, getJobAdLocations } from 'utils/jobAdLocations';

dayjs.extend(relativeTime);
dayjs.extend(utc);

const userTimeZone = dayjs.tz?.guess();

const getFirstSentence = (text) => {
  if (!text) return '';
  const firstSentenceMatch = text.match(/[^.!?]+[.!?]/);
  return firstSentenceMatch ? firstSentenceMatch[0].trim() : text.trim();
};

export const RelatedJobAdsListItem = ({
  ad_id,
  headline,
  slug,
  reference_number,
  status,
  end_date,
  buyer_id,
  buyer_name,
  buyer_slug,
  supplier_id,
  supplier_slug,
  total_no_versions,
  buyer_logo_url,
  locations,
  places,
  supplier_name,
  supplier_logo_url,
  last_day_to_respond,
  role,
  competence_areas,
  created_on,
  description
}) => {
  const { t, i18n } = useTranslation();
  const { account, user } = useAuth();

  const adjustedDate = localizeRelativeTime(dayjs.utc(created_on).tz(userTimeZone, true), i18n.language);
  const [timeAgo, setTimeAgo] = useState('');

  useEffect(() => {
    const intervalId = setInterval(() => {
      setTimeAgo(adjustedDate.fromNow());
    }, 60000);

    setTimeAgo(adjustedDate.fromNow());

    return () => clearInterval(intervalId);
  }, [adjustedDate]);

  const displayLocations = getJobAdLocations({ locations, places });

  const locationsTooltip = (
    <>
      {displayLocations.map((loc) => (
        <div key={loc.id}>{formatJobAdLocation(loc)}</div>
      ))}
    </>
  );

  const locationsBaseText = displayLocations.length === 1 ? formatJobAdLocationShort(displayLocations[0]) : t('website:jobAdCard.multiple');

  let locationsDisplayText;
  if (locationsBaseText.length > 10) {
    locationsDisplayText = `${locationsBaseText.slice(0, 10)}...`;
  } else {
    locationsDisplayText = `${locationsBaseText}`;
  }

  const areasTooltip = (
    <>
      {competence_areas?.map((area) => (
        <div key={area.id}>{area.name}</div>
      ))}
    </>
  );

  const areasBaseText = competence_areas ? competence_areas.length === 1 ? `${competence_areas[0]?.name}` : t('website:jobAdCard.multiple') : '';

  let areasDisplayText;
  if (areasBaseText.length > 15) {
    areasDisplayText = `${areasBaseText.slice(0, 15)}...`;
  } else {
    areasDisplayText = `${areasBaseText}`;
  }

  const buyer_url = (account?.super_admin || user?.Company?.company_id === buyer_id ) ? paths.buyersDetailsView(buyer_id) : buyer_slug?.length > 0 ? paths.public.companyPublicProfile("buyer", buyer_slug) : "";
  const supplier_url = (account?.super_admin || user?.Company?.company_id === supplier_id ) ? paths.suppliersDetailsView(supplier_id) : supplier_slug?.length > 0 ? paths.public.companyPublicProfile("supplier", supplier_slug) : "";
  const jobAd_url = (account?.super_admin || user?.Company?.company_id) ? paths.jobAdsDetailsView(ad_id) : slug?.length > 0 ? paths.public.jobAdsDetailsView(slug) : "";  
  
  return (
    <ListCard
      addDefaultStyling = {false}
      sx={{
        borderRadius: 1,
        borderColor:"border.jobAd",
        flexDirection: "row",
        px: 2,
        py: 2,
        gap: 2,
        boxShadow: 'none',
      }}
    >
      <Stack
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: 1,
          flex: "1 0 0",
        }}
      >
        <Stack
          sx={{
            flexDirection: "row",
            alignItems: "flex-start",
            gap: {md: 2, lg: 5},
            '& > *': {
              borderRight: '2px solid',
              borderColor: 'border.main',
              pr: {md: 2, lg: 5},
            },
            display: {xs: "none", md: "flex"}
          }}
        >
          <Stack
            sx={{
              flexDirection: "column",
              gap: 0.375,
              height: 1,
            }}
          >
            <Typography
              variant="caption"
              color="text.black"
              fontWeight={500}
              letterSpacing={0}
            >
              {t('website:jobAdCard.applyBefore')}
            </Typography>
            <Typography
              variant="body3"
              color="text.custom"
              letterSpacing={0}
              lineHeight={"200%"}
            >
              {isoToDateString(last_day_to_respond, false)}
            </Typography>
          </Stack>
          <Stack
            sx={{
              flexDirection: "column",
              gap: 0.375,
              height: 1,
            }}
          >
            <Typography
              variant="caption"
              color="text.black"
              fontWeight={500}
              letterSpacing={0}
            >
              {t('website:jobAdCard.competenceAreas')}
            </Typography>
            {!competence_areas || competence_areas?.length === 0 
            ? 
              <Typography
                variant="body3"
                color="text.custom"
                letterSpacing={0}
                lineHeight={"200%"}
              >
              </Typography>
            :
              <Tooltip title={areasTooltip}>
                <Typography
                  variant="body3"
                  color="text.custom"
                  letterSpacing={0}
                  lineHeight={"200%"}
                >
                  {areasDisplayText}
                </Typography>
              </Tooltip>
            }
          </Stack>
          <Stack
            sx={{
              flexDirection: "column",
              gap: 0.375,
              height: 1,
            }}
          >
            <Typography
              variant="caption"
              color="text.black"
              fontWeight={500}
              letterSpacing={0}
            >
              {t('website:jobAdCard.locations')}
            </Typography>
            {displayLocations.length <= 0 
            ? 
              <Typography
                variant="body3"
                color="text.custom"
                letterSpacing={0}
                lineHeight={"200%"}
              >
              </Typography>
            :
              <Tooltip title={locationsTooltip}>
                <Typography
                  variant="body3"
                  color="text.custom"
                  letterSpacing={0}
                  lineHeight={"200%"}
                >
                  {locationsDisplayText}
                </Typography>
              </Tooltip>
            }
          </Stack>
          <Stack
            sx={{
              flexDirection: "column",
              gap: 0.375,
              height: 1,
            }}
          >
            <Typography
              variant="caption"
              color="text.black"
              fontWeight={500}
              letterSpacing={0}
            >
              {t('website:jobAdCard.published')}
            </Typography>
            <Typography
              variant="body3"
              color="text.custom"
              letterSpacing={0}
              lineHeight={"200%"}
            >
              {timeAgo}
            </Typography>
          </Stack>
        </Stack>
        <Stack
          sx={{
            flexDirection: "row",
            display: "flex",
            gap: 1,
            width: 1,
            alignItems: "center"
          }}
        >
          {buyer_url?.length > 0 ? (
            <Link
              component={RouterLink}
              to={buyer_url}
              color="inherit"
              underline="none"
            >
              <ImageAvatar img={buyer_logo_url || ''} name={buyer_name || ''}
                sx={{
                  width: {xs: 30, md: 40},
                  height: {xs: 30, md: 40},
                  img : {
                    width: {xs: 30, md: 40},
                    height: {xs: 30, md: 40},
                    overflow: 'hidden',
                  }
                }}
              />
            </Link>
          ) : (
            <ImageAvatar img={buyer_logo_url || ''} name={buyer_name || ''}
                sx={{
                  width: {xs: 30, md: 40},
                  height: {xs: 30, md: 40},
                  img : {
                    width: {xs: 30, md: 40},
                    height: {xs: 30, md: 40},
                    overflow: 'hidden',
                  }
                }}
              />
          )}
          <Stack flex={{ md: 2, xl: 4 }} sx={{pr:1}} justifyContent="center">
            <Link
              component={RouterLink}
              to={jobAd_url}
              color="inherit"
              underline="none"
            >
              <Stack flexDirection={"column"} gap={0.5} width={1}>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: {xs: "0.875rem", md: "1.375rem"},
                    fontWeight: {xs: 500, md: 600},
                    lineHeight: "normal",
                    color: "text.black",
                    display: '-webkit-box',
                    overflow: 'hidden',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: 1,
                  }}
                >
                  {headline}
                </Typography>
                <Typography
                  variant="body3"
                  sx={{
                    fontSize: {xs: "0.75rem", md: "1rem"},
                    fontWeight: 400,
                    color: "text.list",
                    display: '-webkit-box',
                    overflow: 'hidden',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: 1,
                  }}
                >
                  {getFirstSentence(description)}
                </Typography>
              </Stack>
            </Link>
          </Stack>
        </Stack>
      </Stack>
      <Stack
        justifyContent={"flex-end"}
      >
        {supplier_url?.length > 0 ? (
          <Link
            component={RouterLink}
            to={supplier_url}
            color="inherit"
            underline="none"
          >
            <ImageAvatar img={supplier_logo_url || ''} name={supplier_name || ''} fontSize='1rem'
              sx={{
                width: {xs: 20, md: 28},
                height: {xs: 20, md: 28},
                img : {
                  width: {xs: 20, md: 28},
                  height: {xs: 20, md: 28},
                  overflow: 'hidden',
                },
                fontSize: {xs: "0.875rem", md: "1rem"}
              }}
            />
          </Link>
        ) : (
          <ImageAvatar img={supplier_logo_url || ''} name={supplier_name || ''} fontSize='1rem'
              sx={{
                width: {xs: 20, md: 28},
                height: {xs: 20, md: 28},
                img : {
                  width: {xs: 20, md: 28},
                  height: {xs: 20, md: 28},
                  overflow: 'hidden',
                },
                fontSize: {xs: "0.875rem", md: "1rem"}
              }}
            />
        )}
      </Stack>
      {/* {account.super_admin && (
        <StatusComponent status={status} additionalColor />
      )} */}
    </ListCard>
  );
};
